"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Search, X, Loader2, SlidersHorizontal, ChevronDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Kbd } from "@/components/ui/kbd";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SEARCHABLE_FIELDS, type SearchableField } from "@/lib/search/schema";

const FIELD_LABELS: Record<SearchableField, { label: string; hint: string }> = {
  title: { label: "Title", hint: "Strongest match weight" },
  author: { label: "Author", hint: "Author handle" },
  ai_summary: { label: "AI summary", hint: "Generated précis" },
  description: { label: "Description", hint: "Intro paragraph" },
  effects_text: { label: "Effects", hint: "Every effect and sub-effect" },
  sections_text: { label: "Sections", hint: "Drawbacks, notes, equipment…" },
};

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  fields: SearchableField[];
  onFieldsChange: (fields: SearchableField[]) => void;
  debounceMs?: number;
  placeholder?: string;
  isSearching?: boolean;
  className?: string;
}

/**
 * The search lens: the one glowing element on the browse screen. Field
 * narrowing and advanced syntax sit behind a single disclosure so the bar
 * itself stays quiet.
 */
export function SearchInput({
  value,
  onChange,
  fields,
  onFieldsChange,
  debounceMs = 250,
  placeholder = "Search Essences…",
  isSearching = false,
  className,
}: SearchInputProps) {
  const [internal, setInternal] = useState(value);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputId = useId();

  // The prop value is the URL-driven query. The input keeps its own draft so
  // typing stays responsive and the cursor never jumps. We sync the prop into
  // the draft only for *external* changes (back/forward, Clear all), never
  // while the user has unacknowledged edits in flight.
  const lastExternalValue = useRef(value);
  const inFlightValues = useRef<Set<string>>(new Set());
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (inFlightValues.current.has(value)) {
      // Parent acknowledged an edit we sent; drop it from the in-flight set.
      inFlightValues.current.delete(value);
      lastExternalValue.current = value;
      return;
    }

    // While edits are in flight, ignore external prop changes so the cursor
    // and draft stay under the user's control. Once the queue is empty, sync
    // any new external value into the input.
    if (inFlightValues.current.size > 0) return;

    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      setInternal(value);
    }
  }, [value]);

  const flushChange = useCallback((next: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    inFlightValues.current.clear();
    if (next !== lastExternalValue.current) {
      lastExternalValue.current = next;
      onChangeRef.current(next);
    }
  }, []);

  const queueChange = useCallback(
    (next: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (next !== lastExternalValue.current) {
          inFlightValues.current.clear();
          inFlightValues.current.add(next);
          onChangeRef.current(next);
        }
      }, debounceMs);
    },
    [debounceMs]
  );

  const updateInternal = useCallback(
    (next: string) => {
      setInternal(next);
      queueChange(next);
    },
    [queueChange]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // "/" focuses search from anywhere that is not already a text field.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const narrowed = fields.length > 0 && fields.length < SEARCHABLE_FIELDS.length;

  return (
    <div
      className={cn(
        "group/lens relative flex h-12 items-center rounded-full border bg-[rgb(16_14_27/0.75)] pl-4 pr-1.5 backdrop-blur-md",
        "transition-[border-color,box-shadow,background-color] duration-200",
        focused
          ? "border-line-accent shadow-[var(--shadow-glow)]"
          : "border-line-strong shadow-[0_8px_30px_-16px_rgb(0_0_0/0.8)] hover:border-[rgb(167_139_250/0.3)]",
        className
      )}
    >
      <label htmlFor={inputId} className="sr-only">
        Search Essences
      </label>
      {isSearching ? (
        <Loader2 className="size-[18px] shrink-0 animate-spin text-accent" aria-hidden />
      ) : (
        <Search
          className={cn(
            "size-[18px] shrink-0 transition-colors",
            focused ? "text-accent" : "text-ink-3"
          )}
          aria-hidden
        />
      )}
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={internal}
        onChange={(e) => updateInternal(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            if (internal) {
              setInternal("");
              flushChange("");
            } else {
              inputRef.current?.blur();
            }
          }
          if (e.key === "Enter") {
            flushChange(internal);
          }
        }}
        className="h-full min-w-0 flex-1 bg-transparent px-3 text-[15px] text-ink outline-none placeholder:text-ink-3"
      />

      {internal && (
        <button
          type="button"
          onClick={() => {
            setInternal("");
            flushChange("");
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
        >
          <X className="size-4" />
        </button>
      )}

      {!focused && !internal && (
        <Kbd className="mr-1 hidden lg:inline-flex" aria-hidden>
          /
        </Kbd>
      )}

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Search options"
            className={cn(
              "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[12.5px] font-medium transition-colors",
              "focus-visible:outline-2 focus-visible:outline-accent data-[state=open]:border-line-accent data-[state=open]:bg-accent-tint",
              narrowed
                ? "border-[rgb(167_139_250/0.4)] bg-accent-tint text-accent"
                : "border-transparent text-ink-3 hover:border-line hover:bg-surface-2 hover:text-ink-2"
            )}
          >
            <SlidersHorizontal className="size-3.5" />
            <span className="hidden sm:inline">
              {narrowed ? `${fields.length} fields` : "Fields"}
            </span>
            <ChevronDown className="size-3 opacity-70" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
            <span className="micro-label">Search in</span>
            {narrowed && (
              <button
                type="button"
                onClick={() => onFieldsChange([])}
                className="text-[12px] font-medium text-accent hover:underline"
              >
                All fields
              </button>
            )}
          </div>
          <ul className="px-2 pb-2">
            {SEARCHABLE_FIELDS.map((field) => {
              const checked = fields.length === 0 || fields.includes(field);
              return (
                <li key={field}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-surface-2">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => {
                        // Starting from "all", unchecking one narrows to the rest.
                        const base = fields.length === 0 ? [...SEARCHABLE_FIELDS] : fields;
                        const next = base.includes(field)
                          ? base.filter((f) => f !== field)
                          : [...base, field];
                        onFieldsChange(next.length === SEARCHABLE_FIELDS.length ? [] : next);
                      }}
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-[13.5px] text-ink">{FIELD_LABELS[field].label}</span>
                      <span className="text-[11.5px] text-ink-3">{FIELD_LABELS[field].hint}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-line px-4 py-3">
            <span className="micro-label">Advanced syntax</span>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12.5px]">
              <dt>
                <code className="rounded bg-surface-3 px-1 py-px font-mono text-[11.5px] text-accent">
                  author:name
                </code>
              </dt>
              <dd className="text-ink-3">Match a field directly</dd>
              <dt>
                <code className="rounded bg-surface-3 px-1 py-px font-mono text-[11.5px] text-accent">
                  title:&quot;two words&quot;
                </code>
              </dt>
              <dd className="text-ink-3">Quote phrases with spaces</dd>
              <dt>
                <code className="rounded bg-surface-3 px-1 py-px font-mono text-[11.5px] text-accent">
                  effects_text:…
                </code>
              </dt>
              <dd className="text-ink-3">
                Also: <span className="font-mono">description</span>,{" "}
                <span className="font-mono">ai_summary</span>,{" "}
                <span className="font-mono">sections_text</span>
              </dd>
            </dl>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
